import * as React from 'react'
import { flushSync } from 'react-dom'
import { ScheduleModel } from 'app/models/timesheet/scheduleModel'
import { ScheduleDayModel } from 'app/models/timesheet/scheduleDayModel'
import DayWrapper from './day/DayWrapper'
import { ScheduleHeaderItem } from './shared/ScheduleHeaderItem'
import { ScheduleCalendarMonths } from './month/ScheduleCalendarMonths'
import { useSchedule } from '../context/ScheduleContext'
import ScheduleContextMenu from './contextMenu/ScheduleContextMenu'

import DayTypesModal from './modal/DayTypesModal'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { DayType } from './constants/DayType'
import useDayTypesService from '@/modules/drivers/services/DayTypesService'
import DayScheduleModal from './modal/DayScheduleModal'

export type ScheduleCalendarProps = {
  schedule: ScheduleModel
  driverId: number
  companyId: string
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  schedule,
  driverId,
  companyId,
}) => {
  const [selectedDay, setSelectedDay] = React.useState<ScheduleDayModel | null>(
    null,
  )
  const {
    setSelectedDays,
    selectedDays,
    setIsOpenDayTypeModal,
    isOpenDayTypeModal,
    isLoading,
    refresh
  } = useSchedule()
  const { getPinnedDays } = useDayTypesService()
  const [isOpenDayPlanModal, setIsOpenDayPlanModal] = React.useState(false)
  const [pinnedDays, setPinnedDays] = React.useState<DayType[]>([])
  const [isPinnedLoading, setIsPinnedLoading] = React.useState(true)
  const [contextMenuOpen, setContextMenuOpen] = React.useState(false)
  const [contextMenuPosition, setContextMenuPosition] = React.useState({
    x: 0,
    y: 0,
  })

  const fetchPinnedDays = React.useCallback(async () => {
    setIsPinnedLoading(true)
    try {
      const data = await getPinnedDays({ companyId })
      setPinnedDays(data)
    } catch (error) {
      console.error('Error fetching pinned days:', error)
    } finally {
      setIsPinnedLoading(false)
    }
  }, [companyId])

  React.useEffect(() => {
    fetchPinnedDays()
  }, [])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const lastClickedDayRef = React.useRef<ScheduleDayModel | null>(null)
  const isDraggingRef = React.useRef(false)
  const dragStartDayRef = React.useRef<string | null>(null)

  const days = schedule && schedule.days
  const periods = schedule && schedule.periods
  const hoursInMonth = schedule && schedule.hoursInMonth

  const MONTHS_WIDTH = 36
  const PERIODS_WIDTH = 43
  const DAY_COLUMNS = 37
  const MIN_DAY_WIDTH = 30
  const DAY_GAP = 0

  const [dayWidth, setDayWidth] = React.useState<number>(MIN_DAY_WIDTH)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const available = entry.contentRect.width - MONTHS_WIDTH - PERIODS_WIDTH
      const calculated = Math.floor(available / DAY_COLUMNS) - DAY_GAP
      setDayWidth(Math.max(MIN_DAY_WIDTH, calculated))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const flatDays = React.useMemo(() => {
    if (!days) return []
    return days.flat().filter((d): d is ScheduleDayModel => d !== null)
  }, [days])

  const getDayIndex = React.useCallback(
    (dayId: string) => {
      return flatDays.findIndex((d) => d.dayDate.format('YYYY-MM-DD') === dayId)
    },
    [flatDays],
  )

  React.useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  const handleDayMouseDown = React.useCallback(
    (day: ScheduleDayModel, e: React.MouseEvent) => {
      if (e.button !== 0) return
      isDraggingRef.current = true
      dragStartDayRef.current = day.dayDate.format('YYYY-MM-DD')
    },
    [],
  )

  const handleDayMouseEnter = React.useCallback(
    (day: ScheduleDayModel) => {
      if (!isDraggingRef.current || !dragStartDayRef.current) return
      const startIdx = getDayIndex(dragStartDayRef.current)
      const endIdx = getDayIndex(day.dayDate.format('YYYY-MM-DD'))
      if (startIdx === -1 || endIdx === -1) return
      const min = Math.min(startIdx, endIdx)
      const max = Math.max(startIdx, endIdx)
      const rangeDays = flatDays
        .slice(min, max + 1)
        .map((d) => d.dayDate.format('YYYY-MM-DD'))
      setSelectedDays(rangeDays)
      lastClickedDayRef.current = flatDays[startIdx] ?? null
    },
    [flatDays, getDayIndex, setSelectedDays],
  )

  const handleDayContextMenu = React.useCallback(
    (day: ScheduleDayModel, e: React.MouseEvent) => {
      if (!day) return
      e.preventDefault()
      e.stopPropagation()
      const dayId = day.dayDate.format('YYYY-MM-DD')
      flushSync(() => {
        setSelectedDays((prev) => {
          if (prev.includes(dayId)) return prev
          return [dayId]
        })
        lastClickedDayRef.current = day
        setSelectedDay(day)
      })
      setContextMenuPosition({ x: e.clientX, y: e.clientY })
      setContextMenuOpen(true)
    },
    [setSelectedDays],
  )

  const prepareHeader = (): Array<number> => {
    const header: Array<number> = []
    for (let i = 0; i < 37; i++) {
      header.push(i % 7)
    }
    return header
  }

  const ROW_HEIGHT = 88

  const header = prepareHeader()

  const handleDaySelected = (
    daySelected: ScheduleDayModel,
    event?: React.MouseEvent,
  ) => {
    if (daySelected == null) {
      return
    }

    const dayId = daySelected.dayDate.format('YYYY-MM-DD')
    const isCtrlOrCmd = event?.ctrlKey || event?.metaKey
    const isShift = event?.shiftKey

    if (isCtrlOrCmd) {
      setSelectedDays((prev) => {
        if (prev.includes(dayId)) {
          return prev.filter((id) => id !== dayId)
        } else {
          return [...prev, dayId]
        }
      })
      lastClickedDayRef.current = daySelected
    } else if (isShift && lastClickedDayRef.current && days) {
      const allDays = days
        .flat()
        .filter((d): d is ScheduleDayModel => d !== null)
      const lastIndex = allDays.findIndex(
        (d) =>
          d.dayDate.format('YYYY-MM-DD') ===
          lastClickedDayRef.current?.dayDate.format('YYYY-MM-DD'),
      )
      const currentIndex = allDays.findIndex(
        (d) => d.dayDate.format('YYYY-MM-DD') === dayId,
      )

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex)
        const end = Math.max(lastIndex, currentIndex)
        const rangeDays = allDays
          .slice(start, end + 1)
          .map((d) => d.dayDate.format('YYYY-MM-DD'))

        setSelectedDays((prev) => {
          const newSet = new Set([...prev, ...rangeDays])
          return Array.from(newSet)
        })
      }
    } else {
      setSelectedDays([dayId])
      lastClickedDayRef.current = daySelected
    }

    setSelectedDay(daySelected)
  }

  const renderMonths = React.useCallback(
    (height: number, width: number, periods: any[]) => {
      return (
        <div>
          <div
            className="tw-ml-1.5 tw-h-[35px] tw-w-[35px] tw-border-b tw-border-base-300"
            style={{
              width: 56,
              height: 36,
            }}
          />
          <ScheduleCalendarMonths
            height={height}
            width={width}
            periods={periods}
          />
        </div>
      )
    },
    [],
  )

  const renderHeader = React.useCallback((header: number[], width: number) => {
    return (
      <div className="tw-mt-0 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-base-300">
        {header.map((item, index) => {
          return (
            <ScheduleHeaderItem
              key={index}
              dayOfWeek={item}
              height={35}
              width={width}
            />
          )
        })}
      </div>
    )
  }, [])

  const renderCalendarBody = React.useCallback(
    (days: ScheduleDayModel[][], rowHeight: number, width: number) => {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          {days &&
            days.map((item, itemIndex) => {
              return (
                <div
                  key={itemIndex}
                  className="tw-flex tw-items-center tw-justify-center"
                  style={{ height: rowHeight }}
                >
                  {item.map((day: ScheduleDayModel, dayIndex) => {
                    const prevDay = dayIndex > 0 ? item[dayIndex - 1] : null
                    const nextDay =
                      dayIndex < item.length - 1 ? item[dayIndex + 1] : null
                    const sameDayType = day?.dayType
                    const isFirstInGroup =
                      !prevDay || prevDay.dayType !== sameDayType
                    const isLastInGroup =
                      !nextDay || nextDay.dayType !== sameDayType

                    const dayId = day?.dayDate.format('YYYY-MM-DD')
                    const isSelectedInContext = dayId
                      ? selectedDays.includes(dayId)
                      : false

                    return (
                      <DayWrapper
                        onSelected={handleDaySelected}
                        onMouseDown={handleDayMouseDown}
                        onMouseEnter={handleDayMouseEnter}
                        onContextMenu={handleDayContextMenu}
                        isSelected={isSelectedInContext}
                        key={dayIndex}
                        day={day}
                        height={rowHeight}
                        width={width}
                        isFirstInGroup={isFirstInGroup}
                        isLastInGroup={isLastInGroup}
                      />
                    )
                  })}
                </div>
              )
            })}
        </div>
      )
    },
    [
      selectedDay,
      handleDaySelected,
      handleDayMouseDown,
      handleDayMouseEnter,
      handleDayContextMenu,
    ],
  )

  return (
    <div
      ref={containerRef}
      className="tw-w-full tw-overflow-x-auto"
      style={{ maxWidth: 2000 }}
    >
      {isLoading && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
          <LoaderIcon className="tw-animate-spin" />
        </div>
      )}
      {days && days.length > 0 && (
        <div className="tw-flex tw-min-w-fit tw-items-start tw-justify-start tw-gap-0">
          {renderMonths(ROW_HEIGHT, 35, periods)}

          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
            {renderHeader(header, dayWidth)}
            {renderCalendarBody(days, ROW_HEIGHT, dayWidth)}
          </div>
        </div>
      )}

      <ScheduleContextMenu
        isOpen={contextMenuOpen}
        setIsOpenDayPlanModal={() => setIsOpenDayPlanModal(true)}
        position={contextMenuPosition}
        onClose={() => setContextMenuOpen(false)}
        driverId={driverId}
        companyId={companyId}
        isPinnedLoading={isPinnedLoading}
        pinnedDays={pinnedDays}
        fetchPinnedDays={fetchPinnedDays}
      />
      {isOpenDayTypeModal && (
        <DayTypesModal
          close={() => setIsOpenDayTypeModal(false)}
          companyId={companyId}
          driverId={driverId}
        />
      )}
      {isOpenDayPlanModal && (
        <DayScheduleModal
          refreshSchedule={refresh}
          close={() => setIsOpenDayPlanModal(false)}
          companyId={companyId}
          driverId={driverId}
          selectedDays={flatDays.filter((d) =>
            selectedDays.includes(d.dayDate.format('YYYY-MM-DD')),
          )}
        />
      )}
    </div>
  )
}
